import { Box, SimpleGrid, Text, UnstyledButton } from "@mantine/core"
import { useTranslation } from "react-i18next"
import classes from "./RouteStatsCards.module.css"
import { RouteQuickFilter } from "./RouteFiltersToolbar"

interface Props {
  counts: {
    all: number
    waitingDriver: number
    picking: number
    handoverReady: number
    pretripPending: number
  }
  value: RouteQuickFilter
  onChange: (next: RouteQuickFilter) => void
}

/**
 * Loading stages of a PLANNED route, in the order a route moves through them
 * (route-progress.ts). Each tile is also the filter for that stage, so the
 * numbers always add up to "Бүгд" and a click shows exactly those routes.
 */
export function RouteStatsCards({ counts, value, onChange }: Props) {
  const { t } = useTranslation(["delivery"])

  const tiles: Array<{ key: RouteQuickFilter; step?: number; label: string; count: number; meta: string }> = [
    { key: "ALL", label: t("delivery:route.stats.all"), count: counts.all, meta: t("delivery:route.stats.allMeta") },
    {
      key: "WAITING_DRIVER",
      step: 1,
      label: t("delivery:route.stats.waiting"),
      count: counts.waitingDriver,
      meta: counts.waitingDriver > 0 ? t("delivery:route.stats.waitingMeta") : t("delivery:route.stats.waitingNone"),
    },
    { key: "PICKING", step: 2, label: t("delivery:route.stats.picking"), count: counts.picking, meta: t("delivery:route.stats.pickingMeta") },
    {
      key: "HANDOVER_READY",
      step: 3,
      label: t("delivery:route.stats.handover"),
      count: counts.handoverReady,
      meta: t("delivery:route.stats.handoverMeta"),
    },
    {
      key: "PRETRIP_PENDING",
      step: 4,
      label: t("delivery:route.stats.pretrip"),
      count: counts.pretripPending,
      meta: t("delivery:route.stats.pretripMeta"),
    },
  ]

  return (
    <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
      {tiles.map((tile) => {
        const active = value === tile.key
        return (
          <UnstyledButton
            key={tile.key}
            className={classes.stageTile}
            data-active={active || undefined}
            data-empty={tile.count === 0 || undefined}
            aria-pressed={active}
            onClick={() => onChange(active && tile.key !== "ALL" ? "ALL" : tile.key)}
          >
            <Text component="span" className={classes.stageTileLabel}>
              {tile.step && (
                <Box component="span" className={classes.stageTileStep}>
                  {tile.step}
                </Box>
              )}
              {tile.label}
            </Text>
            <Text component="span" className={classes.statValue}>
              {tile.count}
            </Text>
            <Text component="span" className={classes.stageTileMeta}>
              {tile.meta}
            </Text>
          </UnstyledButton>
        )
      })}
    </SimpleGrid>
  )
}
